/**
 * Reports Store (Zustand)
 *
 * State management for the Reports module.
 */
import { create } from 'zustand';
import fetchWithTenant from '../utils/fetchWithTenant';

const API_BASE = '/api/reports';

const useReportsStore = create((set, get) => ({
  // State
  reportTypes: [],
  reportRuns: [],
  runsTotal: 0,
  currentRun: null,
  currentReportData: null,
  templates: [],
  schedules: [],
  loading: false,
  generating: false,
  error: null,
  activeTab: 'library',
  selectedType: null,
  pollInterval: null,

  // Actions
  setActiveTab: (tab) => set({ activeTab: tab }),
  setSelectedType: (type) => set({ selectedType: type }),
  clearError: () => set({ error: null }),

  fetchReportTypes: async () => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/types`);
      if (!res.ok) throw new Error('Failed to fetch report types');
      const data = await res.json();
      set({ reportTypes: data.types || [] });
    } catch (err) {
      set({ error: err.message });
    }
  },

  generateReport: async (type, parameters, format) => {
    set({ generating: true, error: null });
    try {
      const res = await fetchWithTenant(`${API_BASE}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type, parameters, format }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to generate report');
      }
      const data = await res.json();
      set({ generating: false });

      // Start polling for completion
      get().startPolling(data.run.id);

      // Refresh runs list
      get().fetchReportRuns();

      return data.run;
    } catch (err) {
      set({ generating: false, error: err.message });
      throw err;
    }
  },

  fetchReportRuns: async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.type) params.set('type', options.type);
      if (options.status) params.set('status', options.status);
      if (options.limit) params.set('limit', options.limit);
      if (options.offset) params.set('offset', options.offset);

      const res = await fetchWithTenant(`${API_BASE}/runs?${params}`);
      if (!res.ok) throw new Error('Failed to fetch report runs');
      const data = await res.json();
      set({ reportRuns: data.runs || [], runsTotal: data.total || 0 });
    } catch (err) {
      set({ error: err.message });
    }
  },

  fetchReportRun: async (runId) => {
    set({ loading: true, error: null });
    try {
      const res = await fetchWithTenant(`${API_BASE}/runs/${runId}`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const data = await res.json();
      set({
        loading: false,
        currentRun: data.run,
        currentReportData: data.reportData,
      });
      return data;
    } catch (err) {
      set({ loading: false, error: err.message });
    }
  },

  startPolling: (runId) => {
    const existingInterval = get().pollInterval;
    if (existingInterval) clearInterval(existingInterval);

    const interval = setInterval(async () => {
      try {
        const res = await fetchWithTenant(`${API_BASE}/runs/${runId}`);
        if (!res.ok) return;
        const data = await res.json();

        if (data.run.status === 'COMPLETED' || data.run.status === 'FAILED') {
          clearInterval(interval);
          set({ pollInterval: null, currentRun: data.run, currentReportData: data.reportData });
          get().fetchReportRuns();
        }
      } catch {
        // Silently retry
      }
    }, 3000);

    set({ pollInterval: interval });
  },

  stopPolling: () => {
    const interval = get().pollInterval;
    if (interval) {
      clearInterval(interval);
      set({ pollInterval: null });
    }
  },

  deleteReport: async (runId) => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/runs/${runId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete report');
      get().fetchReportRuns();
    } catch (err) {
      set({ error: err.message });
    }
  },

  downloadReport: async (runId, format = 'pdf') => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/runs/${runId}/download?format=${format}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to download');
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `report.${format}`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      set({ error: err.message });
    }
  },

  // Templates
  fetchTemplates: async () => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/templates`);
      if (!res.ok) throw new Error('Failed to fetch templates');
      const data = await res.json();
      set({ templates: data.templates || [] });
    } catch (err) {
      set({ error: err.message });
    }
  },

  createTemplate: async (templateData) => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(templateData),
      });
      if (!res.ok) throw new Error('Failed to create template');
      get().fetchTemplates();
    } catch (err) {
      set({ error: err.message });
    }
  },

  deleteTemplate: async (templateId) => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete template');
      get().fetchTemplates();
    } catch (err) {
      set({ error: err.message });
    }
  },

  // Schedules
  fetchSchedules: async () => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/schedules`);
      if (!res.ok) throw new Error('Failed to fetch schedules');
      const data = await res.json();
      set({ schedules: data.schedules || [] });
    } catch (err) {
      set({ error: err.message });
    }
  },

  createSchedule: async (scheduleData) => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(scheduleData),
      });
      if (!res.ok) throw new Error('Failed to create schedule');
      get().fetchSchedules();
    } catch (err) {
      set({ error: err.message });
    }
  },

  updateSchedule: async (scheduleId, updates) => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/schedules/${scheduleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('Failed to update schedule');
      get().fetchSchedules();
    } catch (err) {
      set({ error: err.message });
    }
  },

  deleteSchedule: async (scheduleId) => {
    try {
      const res = await fetchWithTenant(`${API_BASE}/schedules/${scheduleId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete schedule');
      get().fetchSchedules();
    } catch (err) {
      set({ error: err.message });
    }
  },

  // Cleanup
  reset: () => {
    get().stopPolling();
    set({
      currentRun: null,
      currentReportData: null,
      selectedType: null,
      error: null,
    });
  },
}));

export default useReportsStore;
